import type { Diamond } from '../api/types'
import { CertLink } from './CertLink'
import './stoneSpecs.css'

/**
 * STONE SPECS — the shared spec vocabulary for every browse surface
 * (desktop card + table, mobile card + row). One implementation so the two
 * shells cannot drift, and so the gemological rules below are stated once.
 *
 * ── WHAT THE DATA ACTUALLY SUPPORTS ───────────────────────────────────────
 * Censused over 6,000 live stones (2026-08-11): shape, polish, symmetry,
 * fluorescence, colour, clarity, carat, lab, certNo and all three millimetre
 * measurements are at 100%. `cut` is at 100% on ROUNDS and 0–32% on every
 * fancy shape — because labs only issue a cut grade for round brilliants.
 * depthPercent / tablePercent / girdle / crownAngle / pavilionAngle are absent
 * from the catalog entirely.
 *
 * Two consequences drive this whole file:
 *
 *  1. A missing cut on an oval is NOT missing data. VDB renders "Cut: -" down
 *     the entire column for fancies; that is a category error dressed up as a
 *     gap, and copying it was explicitly not the assignment. `specs.cutGraded`
 *     (server-derived) says whether the grade exists for this shape, so a fancy
 *     shape simply shows two marks and a round shows three.
 *
 *  2. Table %, girdle, crown and pavilion are real certificate measurements
 *     the MIGRATED catalog does not hold — but supplier FEEDS supply them
 *     (gap #93, 2026-08-20): depth/table/girdle now arrive on `stone.specs`
 *     (tablePct, girdle) with the raw supplied fields alongside
 *     (`stone.depthPercent` etc.). We render what exists, never a dash, and
 *     for whatever is still absent we say where it lives — we have lab +
 *     certNo at 100% — which is an honest answer and a more useful one than
 *     em-dashes.
 *
 * PROVENANCE: `stone.depthPercent != null` means `specs.depthPct` is the
 * CERTIFICATE's own number — it must NOT wear the "calculated" annotation.
 * Only a derived depth (rounds without a supplied value) is marked Derived.
 *
 * Ratio and depth % arrive pre-computed on `stone.specs`
 * (functions/src/shared/stoneSpecs.ts). This module deliberately has NO
 * client-side fallback formula: duplicating it is exactly how the web card,
 * the mobile card and Gemma would end up quoting three different numbers for
 * one stone. When `specs` is absent (an older cached payload, or before the
 * API deploy lands) those values are simply omitted — which is the same
 * graceful path a stone with incomplete millimetres takes anyway.
 */

/** Grade → the 2-letter code the trade reads. */
const GRADE_CODE: Record<string, string> = {
  Ideal: 'ID',
  Excellent: 'EX',
  'Very Good': 'VG',
  Good: 'GD',
  Fair: 'FR',
  Poor: 'PR',
}

/**
 * Grade → bar fill. Not linear: the gap between Excellent and Very Good is
 * what a buyer is actually deciding on, so it is drawn wider than the gap
 * between Good and Fair. Ideal and Excellent both fill the track (Ideal is a
 * cut grade above Excellent — see shared/heera-mapping.ts::mapCut — and is
 * distinguished by the gold cap, not by length).
 */
const GRADE_FILL: Record<string, number> = {
  Ideal: 1,
  Excellent: 1,
  'Very Good': 0.66,
  Good: 0.42,
  Fair: 0.24,
  Poor: 0.12,
}

/** Grades that read as top-tier — heavier ink, no colour flood. */
const TOP_GRADES = new Set(['Ideal', 'Excellent'])

/** Normalize the server's grade strings (already mapped) plus raw feed forms. */
function normalizeGrade(raw: string | null | undefined): string | null {
  if (!raw) return null
  const t = raw.trim()
  if (!t) return null
  if (GRADE_CODE[t]) return t
  const u = t.toUpperCase()
  if (u === 'ID' || u === 'IDEAL') return 'Ideal'
  if (u === 'EX' || u === 'EXCELLENT') return 'Excellent'
  if (u === 'VG' || u === 'VERY GOOD') return 'Very Good'
  if (u === 'G' || u === 'GD' || u === 'GOOD') return 'Good'
  if (u === 'F' || u === 'FR' || u === 'FAIR') return 'Fair'
  if (u === 'P' || u === 'PR' || u === 'POOR') return 'Poor'
  return null
}

export function gradeCode(raw: string | null | undefined): string | null {
  const g = normalizeGrade(raw)
  return g ? GRADE_CODE[g] : null
}

/**
 * The grade as a WORD, for anything that has to be read aloud. `EX` is a trade
 * abbreviation and a screen reader says it as two letters — "ee ex" — which is
 * not a quality rating in any language. Feeds send both forms ("EX" and
 * "Excellent"), so this normalises either, and passes anything it does not
 * recognise through unchanged rather than deleting a value.
 */
export function gradeWord(raw: string | null | undefined): string | null {
  if (!raw) return null
  return normalizeGrade(raw) ?? raw
}

/**
 * Fluorescence, the same problem: the certificate abbreviations (NON, FNT,
 * MED, STG, VST) are unreadable aloud, and the catalogue carries both forms.
 */
const FLUORESCENCE_WORD: Record<string, string> = {
  NON: 'None', NONE: 'None', N: 'None', NIL: 'None',
  FNT: 'Faint', FAINT: 'Faint', F: 'Faint',
  VSL: 'Very slight', SL: 'Slight', SLT: 'Slight',
  MED: 'Medium', M: 'Medium', MEDIUM: 'Medium',
  STG: 'Strong', ST: 'Strong', S: 'Strong', STRONG: 'Strong',
  VST: 'Very strong', VS: 'Very strong', 'VERY STRONG': 'Very strong',
}

export function fluorescenceWord(raw: string | null | undefined): string | null {
  if (!raw) return null
  const t = raw.trim()
  if (!t) return null
  return FLUORESCENCE_WORD[t.toUpperCase()] ?? t
}

type FinishSlot = {
  label: 'Cut' | 'Polish' | 'Symmetry'
  grade: string | null
  /** True when this shape SHOULD carry the grade but the stone has none. */
  missing: boolean
}

/**
 * The three (or two) finish slots for a stone, shape-aware.
 *
 * Cut is included when the stone HAS one — even on a fancy shape, where a
 * minority of suppliers do fill the column, extra information is welcome. It
 * is included as an explicit hole only when the shape is one that gets graded
 * (a round) and the value is genuinely absent. On a fancy shape with no cut
 * value, the slot is omitted entirely: nothing is missing.
 */
export function finishSlots(stone: Diamond): FinishSlot[] {
  const cut = normalizeGrade(stone.cut)
  const cutGraded = stone.specs?.cutGraded ?? isRoundShape(stone.shape)
  const slots: FinishSlot[] = []
  if (cut || cutGraded) slots.push({ label: 'Cut', grade: cut, missing: !cut })
  slots.push({ label: 'Polish', grade: normalizeGrade(stone.polish), missing: !stone.polish })
  slots.push({ label: 'Symmetry', grade: normalizeGrade(stone.symmetry), missing: !stone.symmetry })
  return slots
}

/** Client-side mirror of the ONE gemological rule, used only when `specs` is
 *  absent from the payload. Not a formula — a shape test. */
function isRoundShape(shape: string | null | undefined): boolean {
  return typeof shape === 'string' && shape.trim().toLowerCase().startsWith('round')
}

/**
 * THE FINISH STRIP — the signature element.
 *
 * Two-letter codes with a fill bar beneath each. The codes are what Tej asked
 * for ("EX EX"); the bars are what make a 50-card grid readable without
 * reading — a solid block of full bars is a triple-excellent stone, spotted
 * before any letter is parsed.
 *
 * Accessibility is not an afterthought here, because "EX EX" is meaningless
 * aloud: every code is aria-hidden and paired with a visually-hidden full
 * sentence, so a screen reader announces "Cut: Excellent. Polish: Excellent."
 * The group also states when a grade does not apply, rather than staying
 * silent about a slot that is missing for a good reason.
 */
export function FinishStrip({
  stone,
  className = '',
  animate = true,
  compact = false,
}: {
  stone: Diamond
  className?: string
  /** Off for dense/virtualized surfaces that re-render on scroll. */
  animate?: boolean
  /** Tighter cells for the narrow mobile table column (~58px for three). */
  compact?: boolean
}) {
  const slots = finishSlots(stone)
  if (slots.length === 0) return null

  const cutGraded = stone.specs?.cutGraded ?? isRoundShape(stone.shape)
  const spoken = slots
    .map((s) => `${s.label}: ${s.grade ?? 'not graded'}`)
    .concat(cutGraded ? [] : ['Cut grade is not issued for this shape'])
    .join('. ')

  return (
    <span
      className={`finish-strip ${className}`}
      data-compact={compact || undefined}
      role="group"
      aria-label={`Finish. ${spoken}`}
    >
      {slots.map((s, i) => {
        const fill = s.grade ? GRADE_FILL[s.grade] ?? 0 : 0
        return (
          <span
            key={s.label}
            className="finish-cell"
            data-top={s.grade ? TOP_GRADES.has(s.grade) : false}
            data-ideal={s.grade === 'Ideal'}
            data-missing={s.missing}
            data-testid={`finish-${s.label.toLowerCase()}`}
          >
            <span className="finish-code" aria-hidden="true">
              {s.grade ? GRADE_CODE[s.grade] : '·'}
            </span>
            <span className="finish-track">
              <span
                className="finish-fill"
                style={
                  {
                    '--fill': fill,
                    '--fill-delay': animate ? `${i * 55}ms` : '0ms',
                  } as React.CSSProperties
                }
              />
            </span>
          </span>
        )
      })}
    </span>
  )
}

/** "6.52 × 6.50 × 4.01 mm", or null when the stone has no measurements. */
export function formatMeasurements(stone: Diamond): string | null {
  const m = stone.measurements
  const ok = (v: number | null | undefined): v is number => typeof v === 'number' && v > 0
  if (!m || !ok(m.length) || !ok(m.width)) return null
  const dims = ok(m.height)
    ? `${m.length} × ${m.width} × ${m.height}`
    : `${m.length} × ${m.width}`
  return `${dims} mm`
}

/**
 * A value we CALCULATED rather than read off the certificate. The dotted
 * underline is the annotation mark; the title/aria spells out the provenance
 * so it is not folklore that a trader has to learn.
 */
function Derived({ children, from }: { children: React.ReactNode; from: string }) {
  return (
    <span className="spec-derived" title={`Calculated from the measurements (${from}) — not transcribed from the certificate`}>
      {children}
    </span>
  )
}

/**
 * The compact metrics line for a CARD: measurements, then the two derived
 * proportions. Renders only what exists — never a placeholder, never a dash.
 */
export function SpecMeta({
  stone,
  className = '',
  stack = false,
}: {
  stone: Diamond
  className?: string
  /**
   * Break the measurements onto their own line. Needed on the mobile card,
   * which is ~180px wide in a two-column grid: on one line the measurements
   * consume it all and truncate away the ratio and depth entirely — i.e. the
   * new information is the first thing lost. Cards have vertical room; use it.
   */
  stack?: boolean
}) {
  const dims = formatMeasurements(stone)
  const ratio = stone.specs?.ratio ?? null
  const depth = stone.specs?.depthPct ?? null
  // Supplied ⇒ the certificate's own number ⇒ no "calculated" annotation.
  const depthSupplied = stone.depthPercent != null
  if (!dims && ratio == null && depth == null) return null

  return (
    <p className={`tabular ${className}`} data-testid="spec-meta">
      {dims && <span className={stack ? 'block truncate' : undefined}>{dims}</span>}
      {dims && !stack && (ratio != null || depth != null) && <span aria-hidden="true"> · </span>}
      {ratio != null && (
        <span>
          <Derived from="length ÷ width">{ratio.toFixed(2)}</Derived>
          <span className="sr-only"> length-to-width ratio</span>
          <span aria-hidden="true"> ratio</span>
        </span>
      )}
      {ratio != null && depth != null && <span aria-hidden="true"> · </span>}
      {depth != null && (
        <span>
          {depthSupplied
            ? <span>{depth.toFixed(1)}%</span>
            : <Derived from="depth ÷ girdle width">{depth.toFixed(1)}%</Derived>}
          <span className="sr-only"> total depth percentage</span>
          <span aria-hidden="true"> depth</span>
        </span>
      )}
    </p>
  )
}

/**
 * THE FULL SPEC SHEET — for the expanded list row and any detail surface.
 *
 * Ends with the honest note about the proportions we do not hold. That note is
 * the whole difference between this and VDB's grid of dashes: it tells a buyer
 * exactly where table %, girdle, crown and pavilion live, using the lab +
 * certificate number we carry on 100% of stones.
 */
export function SpecSheet({ stone, className = '' }: { stone: Diamond; className?: string }) {
  const dims = formatMeasurements(stone)
  const ratio = stone.specs?.ratio ?? null
  const depth = stone.specs?.depthPct ?? null
  const table = stone.specs?.tablePct ?? null
  const girdle = stone.specs?.girdle ?? null
  const crown = stone.specs?.crownAngle ?? null
  const pavilion = stone.specs?.pavilionAngle ?? null
  // Supplied ⇒ the certificate's own number ⇒ no "calculated" annotation.
  const depthSupplied = stone.depthPercent != null
  const cutGraded = stone.specs?.cutGraded ?? isRoundShape(stone.shape)

  const rows: Array<{ label: string; value: React.ReactNode }> = []
  // Colour and clarity are columns on the collapsed row, but a narrow phone
  // truncates "Fancy Intense Yellow" to "Fa…" — so the expansion is the only
  // place a fancy-colour stone's actual grade can be read. Always restate them.
  if (stone.color) rows.push({ label: 'Colour', value: stone.color })
  if (stone.clarity) rows.push({ label: 'Clarity', value: stone.clarity })
  if (stone.fluorescence) rows.push({ label: 'Fluorescence', value: stone.fluorescence })
  if (dims) rows.push({ label: 'Measurements', value: dims })
  if (ratio != null) {
    rows.push({
      label: 'L/W ratio',
      value: <Derived from="length ÷ width">{ratio.toFixed(2)}</Derived>,
    })
  }
  if (depth != null) {
    rows.push({
      label: 'Depth',
      value: depthSupplied
        ? <>{depth.toFixed(1)}%</>
        : <Derived from="depth ÷ girdle width">{depth.toFixed(1)}%</Derived>,
    })
  }
  // Supplied-only certificate proportions (gap #93) — rendered when a feed
  // provided them, omitted otherwise. Never derived, never a dash.
  if (table != null) rows.push({ label: 'Table', value: <>{table.toFixed(1)}%</> })
  if (girdle) rows.push({ label: 'Girdle', value: girdle })
  // Crown/pavilion angles (2026-10-21) — supplied-only certificate data, now
  // published on the specs block. Rendered when a feed provided them, omitted
  // otherwise. Degrees, never derived, never a dash.
  if (crown != null) rows.push({ label: 'Crown angle', value: <>{crown.toFixed(1)}°</> })
  if (pavilion != null) rows.push({ label: 'Pavilion angle', value: <>{pavilion.toFixed(1)}°</> })
  if (stone.stoneType) rows.push({ label: 'Growth', value: stone.stoneType })
  if (stone.origin) rows.push({ label: 'Location', value: stone.origin })

  // What is STILL only on the report — each proportion drops out of the
  // sentence the moment we actually hold its value as a row above.
  const onReport = [
    ...(table == null ? ['table %'] : []),
    ...(girdle ? [] : ['girdle']),
    ...(crown == null ? ['crown'] : []),
    ...(pavilion == null ? ['pavilion angles'] : []),
  ]
  const onReportText = onReport.length
    ? `${onReport.slice(0, -1).join(', ')}${onReport.length > 1 ? ' and ' : ''}${onReport[onReport.length - 1]}`
    : ''

  return (
    <div className={className} data-testid="spec-sheet">
      <dl className="spec-sheet">
        {rows.map((r) => (
          <div key={r.label}>
            <dt>{r.label}</dt>
            <dd>{r.value}</dd>
          </div>
        ))}
      </dl>

      {(stone.lab || stone.certNo) && (
        <p className="spec-note mt-3">
          {!cutGraded && (
            <>
              Labs issue an overall cut grade for round brilliants only — this stone is graded on
              polish and symmetry.{' '}
            </>
          )}
          {onReportText ? (
            <>
              {onReportText.charAt(0).toUpperCase() + onReportText.slice(1)} {onReport.length === 1 ? 'is' : 'are'} on the
              {stone.lab ? ` ${stone.lab}` : ''} report
              {stone.certNo ? <> — certificate <CertLink lab={stone.lab} certNo={stone.certNo} className="tabular font-semibold text-ink" /></> : ''}.
            </>
          ) : (
            <>
              {stone.lab ? `${stone.lab} report` : 'Grading report'}
              {stone.certNo ? <> — certificate <CertLink lab={stone.lab} certNo={stone.certNo} className="tabular font-semibold text-ink" /></> : ''}.
            </>
          )}
        </p>
      )}
    </div>
  )
}
